import React, {Component} from "react";
import TopNav from "../../../common/nav/TopNav";
import Footer from "../../../common/Footer";
import PageBlock from "../../../common/PageBlock";
import CodeBlock from "../../../common/CodeBlock";
import {Link} from "react-router";
import {currentSiteName} from "../../../service/SiteService";

class Advanced extends Component {
    render() {
        return (
            <div>
                <TopNav tab="docs"/>

                <PageBlock>
                    <div className="container">

                        <div className="row">
                            <div className="col-lg-12">
                                <h1 className="page-header">Scripted responses (advanced)</h1>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-sm-12">
                                <p>For more advanced scenarios, you can also control {currentSiteName}'s responses using <a href="https://www.javascript.com/">JavaScript</a> (up to ES5)
                                    or <a href="https://groovy-lang.org/">Groovy</a> scripts. If you choose Groovy, you can of course write plain
                                    Java in your scripts as well.</p>

                                {/* Prerequisites */}
                                <div className="callout m-t-sm">
                                    <div className="callout-content">
                                        <strong>Recommended:</strong> We recommend you start with the simple configuration to
                                        help you understand the basics.
                                    </div>
                                    <div className="callout-secondary">
                                        <Link
                                            to="/docs/mocks/simple"
                                            className="btn btn-sm btn-info">Show simple configuration</Link>
                                    </div>
                                </div>

                                <h3>Your first script</h3>

                                <p>We are going to add a script file to complement our OpenAPI specification.</p>

                                <div className="alert alert-warning">
                                    <h4>
                                        <strong>Important: script naming</strong>
                                    </h4>
                                    <div>
                                        <p>The name of the script file needs to match that of your specification file.</p>
                                        <p>For example, if your specification file is named <code>my-example.yaml</code>, your script file
                                            should be named <code>my-example.groovy</code> or <code>my-example.js</code>.</p>
                                    </div>
                                </div>

                                <h3>Example</h3>
                                <p>Here's a simple example script:</p>
                                <CodeBlock code={`if (context.request.params.action == "create") {
    respond {
        withStatusCode 201
        immediately()
    }
}
`}/>
                                <p>We will explain this syntax later, in the <em>ResponseBehaviour object</em> section. For now, it's enough to know that
                                    the example above causes the mock server to respond with HTTP status code 201 if the value of
                                    the <code>action</code> parameter in the request is <code>create</code>.</p>
                                <p>For example:</p>
                                <CodeBlock code={`HTTP GET https://live.mocks.cloud/some-id/example?action=create
...
201 Created
`}/>
                                <p><em>Tip:</em> The <code>params</code> object used in the script is just a map of the request parameters, so you can use
                                    either
                                    <code>params.yourParamName</code> or <code>params['yourParamName']</code> syntax to access its members.</p>

                                <h3>Another example</h3>
                                <p>Here's a more sophisticated example script:</p>
                                <CodeBlock code={`switch (context.request.params.action) {
    case "create":
        // HTTP Status-Code 201: Created.
        respond {
            withStatusCode 201
            immediately()
        }
        break

    case "fetch":
        // use a static response file and the default mock behaviour
        respond {
            withFile "example-data.json"
            and()
            usingDefaultBehaviour()
        }
        break

    default:
        // default to bad request
        respond {
            withStatusCode 400
            immediately()
        }
        break
}
`}/>
                                <p>In this example, the script causes the mock server to respond with HTTP status codes 200, 201 or 400 depending on
                                    the value of the <code>action</code> parameter in the request.</p>
                                <p>For example:</p>
                                <CodeBlock code={`HTTP GET https://live.mocks.cloud/some-id/example?action=fetch
...
200 OK
...
{
    "hello": "world"
}
`}/>
                                <p>In the case of <code>action=fetch</code>, the script causes the mock server to use the content of the static file
                                    <code>static-data.json</code> to serve the response.</p>
                                <p>And:</p>
                                <CodeBlock code={`HTTP GET https://live.mocks.cloud/some-id/example?action=foo
...
400 Bad Request
`}/>
                                <p>In the default case, the script causes the mock server to return an HTTP 400 response, as shown above.</p>
                                <p>There are many other script objects you could use in order to decide what to return. For example, your script might use the
                                    request method (GET, POST, PUT, DELETE etc.) or other request attributes.</p>

                                <hr/>
                                <h3>Script objects</h3>
                                <p>In order to help you determine what action to take, {currentSiteName} makes certain objects available to your scripts.</p>
                                <table>
                                    <thead>
                                    <tr>
                                        <th>Object</th>
                                        <th>Description</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr>
                                        <td><code>context</code></td>
                                        <td>Convenience object for accessing request properties</td>
                                    </tr>
                                    <tr>
                                        <td><code>config</code></td>
                                        <td>The configuration for the current request</td>
                                    </tr>
                                    <tr>
                                        <td><code>logger</code></td>
                                        <td>Logger, supporting levels such as <code>info(String)</code>, <code>warn(String)</code> etc.</td>
                                    </tr>
                                    </tbody>
                                </table>

                                <h4>Context object</h4>
                                <p>The <code>context</code> object is available to your scripts. It holds things you might like to interrogate,
                                    like the request object.</p>
                                <table>
                                    <thead>
                                    <tr>
                                        <th>Property</th>
                                        <th>Description</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr>
                                        <td><code>request</code></td>
                                        <td>The HTTP request.</td>
                                    </tr>
                                    </tbody>
                                </table>

                                <h4>Request object</h4>
                                <p>The request object is available on the <code>context</code>. It provides access to request parameters, method, URI etc.</p>
                                <table>
                                    <thead>
                                    <tr>
                                        <th>Property</th>
                                        <th>Description</th>
                                        <th>Example</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr>
                                        <td><code>method</code></td>
                                        <td>The HTTP method of the request.</td>
                                        <td><code>"GET"</code></td>
                                    </tr>
                                    <tr>
                                        <td><code>uri</code></td>
                                        <td>The absolute URI of the request.</td>
                                        <td><code>"http://example.com?foo=bar&amp;baz=qux"</code></td>
                                    </tr>
                                    <tr>
                                        <td><code>params</code></td>
                                        <td>A <code>Map</code> containing the request parameters.</td>
                                        <td><code>[ "foo": "bar", "baz": "qux" ]</code></td>
                                    </tr>
                                    <tr>
                                        <td><code>body</code></td>
                                        <td>A <code>String</code> containing the request body.</td>
                                        <td><code>"Hello world."</code></td>
                                    </tr>
                                    </tbody>
                                </table>

                                <h4>ResponseBehaviour object</h4>
                                <p>Your scripts have access to the methods on the <em>ResponseBehaviour</em> object, which
                                    provides a number of methods to enable you to control the mock server response:</p>
                                <table>
                                    <thead>
                                    <tr>
                                        <th>Method</th>
                                        <th>Description</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr>
                                        <td><code>withStatusCode(int)</code></td>
                                        <td>Set the HTTP status code for the response</td>
                                    </tr>
                                    <tr>
                                        <td><code>withFile(String)</code></td>
                                        <td>Respond with the content of a static file</td>
                                    </tr>
                                    <tr>
                                        <td><code>withEmpty()</code></td>
                                        <td>Respond with empty content, or no records</td>
                                    </tr>
                                    <tr>
                                        <td><code>usingDefaultBehaviour()</code></td>
                                        <td>Use {currentSiteName}'s default behaviour to respond</td>
                                    </tr>
                                    <tr>
                                        <td><code>immediately()</code></td>
                                        <td>Skip {currentSiteName}'s default behaviour and respond immediately</td>
                                    </tr>
                                    <tr>
                                        <td><code>and()</code></td>
                                        <td>Syntactic sugar to improve readability of <code>respond</code> statements</td>
                                    </tr>
                                    </tbody>
                                </table>
                                <p>You structure your response behaviours like so:</p>
                                <CodeBlock code={`respond() // ... behaviours go here
`}/>
                                <p>For example:</p>
                                <CodeBlock code={`respond()
    .withStatusCode(201)
    .immediately();
`}/>
                                <p>Or:</p>
                                <CodeBlock code={`respond()
    .withFile('static-data.json')
    .and()
    .usingDefaultBehaviour();
`}/>
                                <hr/>
                                <p><strong>Tip for Groovy users</strong></p>
                                <p>Groovy users can also use this special syntax, for improved readability:</p>
                                <CodeBlock code={`respond {
    // behaviours go here
}
`}/>
                                <p>For example:</p>
                                <CodeBlock code={`respond {
    withStatusCode 201
    and()
    usingDefaultBehaviour()
}
`}/>
                                <hr/>

                                <h3>Returning data from a script</h3>
                                <p>To return data when using a script, you usually specify a response file or send raw data.</p>

                                <h4>Using a response file</h4>
                                <p>More specifically, to specify which response file to use, you explicitly call the <code>withFile(String)</code> method in
                                    your script.</p>
                                <p>Here's an example:</p>
                                <p>
                                    <CodeBlock code={`respond {
    withFile "static-data.json"
}
`}/>
                                </p>
                                <p>In order for the mock server to return the response file in an appropriate format, {currentSiteName} must be allowed to
                                    process it. That means you should not call <code>immediately()</code> unless you want to skip using a response file (e.g. if
                                    you want to send an error code back or a response without a body).</p>
                                <p>Whilst not required, your script could invoke <code>usingDefaultBehaviour()</code> for readability to indicate
                                    that you want {currentSiteName} to handle the response file for you.</p>
                                <p><em>Note: The following block is semantically identical to the one above.</em></p>
                                <p>
                                <CodeBlock code={`respond {
    withFile "static-data.json" and() usingDefaultBehaviour()
}
`}/>
                                </p>

                                <h4>Returning raw data</h4>
                                <p>Finally, you can return raw data using the <code>withData(String)</code> method.</p>

                                <p>
                                    <CodeBlock code={`respond {
    withData '{ "someKey": "someValue" }'
}
`}/>
                                </p>

                            </div>
                        </div>

                    </div>
                </PageBlock>

                <Footer/>
            </div>
        );
    }
}

export default Advanced;



// WEBPACK FOOTER //
// ./src/modules/views/docs/mocks/Advanced.js